// "+ Receipt" on every page of the live books (PRD 21 §6.1): photograph a
// receipt, check it, save it, and carry on with whatever you were doing. It is
// read and matched in the background, and a notification says what became of
// it.
//
// NO `capture` ATTRIBUTE. §6.1 asks for the camera and for the photo library
// (steps 3 and 4). `capture="environment"` opens the camera and hides the
// library; without it a phone offers both, in its own chooser.
//
// WHERE: in the business header on a wide screen, where nothing sits beneath
// it (a floating button there would cover the register dock's own actions,
// which reach the bottom of the window); floating at the bottom on a phone,
// where a thumb reaches and the dock never opens.
//
// WHO SEES IT: whoever may capture a receipt, which the api decides by the
// register's roles, on books that can change. Not on the inbox, which has its
// own "Add receipt".

import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { XStack, YStack } from 'tamagui';
import { GBButton, GBModal, GBText } from '@greenbackapp/ui';
import { useBusinessOptional } from '../../../contexts/BusinessContext';
import { useToast } from '../../../contexts/ToastContext';
import { useBusinessMutability } from '../../../hooks/useBusinessMutability';
import { businessReceiptService } from '../../../services/businessReceiptService';
import { BusinessApiError } from '../../../services/businessApiCommon';
import { BusinessRole } from '../../../types/businessApi';
import { drawThumbnail } from '../../business/documents/drawThumbnail';
import { DocumentThumbnail } from '../../business/documents/DocumentThumbnail';
import { formatBytes } from '../../business/documents/documentRules';

export const RECEIPTS_INBOX_PATH = '/business/banking/receipts';

/** The api's receipt WRITE_ROLES, which are the bank register's. */
const CAPTURE_ROLES: BusinessRole[] = ['owner', 'accountant', 'bookkeeper'];

// On a phone: above the page, beneath every dialog, drawer and toast (the
// kit's layers start at 9000), and clear of the home bar.
const Slot = styled.div`
  display: flex;

  @media (max-width: 767px) {
    position: fixed;
    right: 16px;
    bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    z-index: 900;
  }
`;

const messageOf = (e: unknown, fallback: string): string =>
  e instanceof BusinessApiError || e instanceof Error ? e.message || fallback : fallback;

interface Picked {
  file: File;
  /** An object URL for a photo; null for a PDF, shown by its icon. */
  preview: string | null;
}

/** The button, the chooser and the check, for whoever the gate below lets through. */
export const ReceiptCapture: React.FC = () => {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<Picked | null>(null);
  const [uploading, setUploading] = useState(0);

  // The preview's object URL goes when the preview does.
  useEffect(() => () => {
    if (picked?.preview) URL.revokeObjectURL(picked.preview);
  }, [picked]);

  const choose = () => inputRef.current?.click();

  const pick = (file: File) =>
    setPicked({ file, preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null });

  // Saved in the background: the dialog closes at once, and the toast says
  // when it is in.
  const save = () => {
    if (!picked) return;
    const { file } = picked;
    setPicked(null);
    setUploading(count => count + 1);
    void (async () => {
      try {
        const thumbnail = await drawThumbnail(file);
        const result = await businessReceiptService.capture(file, thumbnail);
        if (result.duplicateOf) {
          toast.showInfo(`You've added this receipt before, as ${result.duplicateOf.filename}. Both are kept.`);
        } else {
          toast.showSuccess("Receipt uploaded. We'll match it to a transaction.");
        }
      } catch (e) {
        toast.showError(messageOf(e, `Could not add ${file.name}`));
      } finally {
        setUploading(count => count - 1);
      }
    })();
  };

  return (
    <>
      <Slot>
        <GBButton variant="primary" size="md" onPress={choose}>
          {uploading > 0 ? 'Adding receipt…' : '+ Receipt'}
        </GBButton>
      </Slot>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        hidden
        aria-label="Photograph or choose a receipt"
        onChange={event => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) pick(file);
        }}
      />
      <GBModal
        visible={picked !== null}
        onDismiss={() => setPicked(null)}
        title="Looks good?"
        size="sm"
        footer={
          <XStack gap={8} justifyContent="flex-end">
            <GBButton
              variant="ghost"
              size="md"
              onPress={() => {
                setPicked(null);
                choose();
              }}
            >
              Retake
            </GBButton>
            <GBButton variant="primary" size="md" onPress={save}>
              Save
            </GBButton>
          </XStack>
        }
      >
        {picked && (
          <YStack gap={8} alignItems="center">
            {picked.preview ? (
              <img
                src={picked.preview}
                alt={`The receipt you chose: ${picked.file.name}`}
                style={{ maxWidth: '100%', maxHeight: '55vh', objectFit: 'contain', borderRadius: 8 }}
              />
            ) : (
              <DocumentThumbnail
                document={{ id: 'picked', filename: picked.file.name, content_type: picked.file.type }}
                size={96}
              />
            )}
            <GBText variant="bodySmall" color="secondary">
              {picked.file.name} · {formatBytes(picked.file.size)}
            </GBText>
            <GBText variant="bodySmall" color="secondary">
              Make sure the total and the date can be read.
            </GBText>
          </YStack>
        )}
      </GBModal>
    </>
  );
};

/** Who sees it, and where: see the top of this file. */
export const ReceiptCaptureButton: React.FC = () => {
  const business = useBusinessOptional();
  const { canMutate } = useBusinessMutability();
  const location = useLocation();

  const role = business?.currentBusiness?.role;
  if (!role || !CAPTURE_ROLES.includes(role) || !canMutate) return null;
  if (location.pathname === RECEIPTS_INBOX_PATH || location.pathname.startsWith(`${RECEIPTS_INBOX_PATH}/`)) {
    return null;
  }
  return <ReceiptCapture />;
};

export default ReceiptCaptureButton;
